import { useEffect, useState } from "react";
import "./UpdateMelding.css";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { RES_TYPES, useFetch } from "@chainless/chainless-react";
import { addLocale, locale } from "primereact/api";
import { NL_PRIMEREACT_LOCALE } from "../sharedUtils";
import { FaTimesCircle } from "react-icons/fa";

addLocale("nl", NL_PRIMEREACT_LOCALE);
locale("nl");

export const UpdateMelding = ({ rowData, refresh }) => {
  const [newPhotos, setNewPhotos] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const [loading, setLoading] = useState(false);
  const maxPhotos = 3;
  const {
    res: patchRes,
    fetchData: patchFetch,
    error: patchError,
  } = useFetch(`/api/meldingen/${rowData.id}`, {
    method: "PATCH",
    resType: RES_TYPES.text,
    lateFetch: true,
  });
  const [tijdsplanning, setTijdsplanning] = useState(
    rowData.tijdsplanning ? new Date(rowData.tijdsplanning * 1000) : null
  );
  const [voltooidOp, setVoltooidOp] = useState(
    rowData.voltooidOp ? new Date(rowData.voltooidOp * 1000) : null
  );
  const [genomenMaatregelen, setGenomenMaatregelen] = useState(
    rowData.genomenMaatregelen || ""
  );
  const [toevoeging, setToevoeging] = useState(rowData.toevoeging || "");
  const [prioriteit, setPrioriteit] = useState(rowData.prioriteit || null);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const checkPhotoLimit = () => {
    if (rowData.foto && rowData.foto.length + newPhotos.length > maxPhotos) {
      setPhotoError(
        `Maximaal ${maxPhotos} foto's toegestaan (bestaande en nieuwe samen)`
      );
    } else {
      setPhotoError("");
    }
  };

  useEffect(() => {
    checkPhotoLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPhotos, rowData.foto]);

  const dateToTimestamp = (date) =>
    date instanceof Date && !isNaN(date)
      ? Math.floor(date.getTime() / 1000)
      : null;

  useEffect(() => {
    if (patchRes && patchRes.ok) {
      refresh();
      setLoading(false);
    } else if (patchError) {
      alert("Fout bij het updaten van een nieuws item", patchError);
      setLoading(false);
    }
  }, [patchRes, patchError, refresh]);

  const handleSave = async () => {
    if (photoError) {
      alert(photoError);
      return;
    }
    let newPhotoBase64s = [];
    if (newPhotos.length > 0) {
      newPhotoBase64s = await Promise.all(newPhotos.map(fileToBase64));
    }
    patchFetch(
      JSON.stringify({
        genomenMaatregelen: genomenMaatregelen,
        toevoeging: toevoeging,
        tijdsplanning: dateToTimestamp(tijdsplanning),
        voltooidOp: dateToTimestamp(voltooidOp),
        prioriteit: prioriteit !== null ? prioriteit : "PRIO_3",
        foto: newPhotoBase64s,
      })
    );
    setLoading(true);
  };

  const prioriteitOptions = [
    { label: "1", value: "PRIO_1" },
    { label: "2", value: "PRIO_2" },
    { label: "3", value: "PRIO_3" },
  ];

  return (
    <div className="update-melding-container">
      <div className="update-melding-row">
        <strong>Tijdsplanning</strong>
        <Calendar
          value={tijdsplanning}
          onChange={(e) => setTijdsplanning(e.value || null)}
          dateFormat="dd-mm-yy"
          placeholder="Kies datum"
          className="update-melding-calendar"
          locale="nl"
        />
      </div>
      <div className="update-melding-row">
        <strong>Voltooid op</strong>
        <Calendar
          value={voltooidOp}
          onChange={(e) => setVoltooidOp(e.value || null)}
          dateFormat="dd-mm-yy"
          placeholder="Kies datum"
          className="update-melding-calendar"
          locale="nl"
        />
      </div>
      <div className="update-melding-row">
        <strong>Genomen maatregelen</strong>
        <InputText
          value={genomenMaatregelen}
          onChange={(e) => setGenomenMaatregelen(e.target.value)}
          className="update-melding-input"
          placeholder="Genomen maatregelen"
        />
      </div>
      <div className="update-melding-row">
        <strong>Toevoeging</strong>
        <InputText
          value={toevoeging}
          onChange={(e) => setToevoeging(e.target.value)}
          className="update-melding-input"
          placeholder="Toevoeging"
        />
      </div>
      <div className="update-melding-row">
        <strong data-testid="fotos-input-label">
          Nieuwe foto&#39;s toevoegen
        </strong>
        <div>
          <label htmlFor="image_uploads" className="foto-input-label">
            Bladeren...
          </label>
          <input
            type="file"
            id="image_uploads"
            name="image_uploads"
            accept=".jpg, .jpeg, .png"
            className="update-melding-input"
            style={{ display: "none" }}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              setNewPhotos(files);
            }}
            multiple
          />
        </div>
        {photoError && <div className="foto-input-error">{photoError}</div>}
        {newPhotos.length > 0 && (
          <div className="foto-new-preview-container">
            {newPhotos.map((file, idx) => (
              <div key={idx} className="new-foto-content">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="new-foto"
                />
                <FaTimesCircle
                  className="remove-new-foto-icon"
                  onClick={() => {
                    const updatedPhotos = newPhotos.filter((_, i) => i !== idx);
                    setNewPhotos(updatedPhotos);
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="update-melding-row">
        <strong>Prioriteit</strong>
        <Dropdown
          value={prioriteit}
          options={prioriteitOptions}
          onChange={(e) => setPrioriteit(e.value)}
          placeholder="Selecteer prioriteit"
          className="update-melding-dropdown"
        />
      </div>
      <button
        onClick={handleSave}
        className="update-melding-save-btn"
        disabled={loading}
      >
        {loading ? "versturen..." : "Opslaan"}
      </button>
    </div>
  );
};
